/**
 * MarkdownView — themed markdown renderer.
 *
 * Renders the markdown subset the app actually ships (headings, paragraphs
 * with soft breaks, bullet and numbered lists, blockquotes, rules, and the
 * inline set `**bold**` / `*italic*` / `` `code` `` / `[text](href)`) with
 * Tago's typography tokens, so the output stays consistent with TextInput /
 * LegalSection / form labels elsewhere in the app.
 *
 * It used to wrap `react-native-markdown-display`, and that took every legal
 * screen down: markdown-it imports `linkify-it`, which ships ESM-first from
 * v5, and Metro/Hermes hands back a namespace object whose `.default` is not
 * a constructor. `new MarkdownIt()` threw at module scope, so Terms of
 * Service, Privacy Policy, Cancellation Policy and the three Profile routes
 * that redirect into them all rendered the crash boundary instead of a word
 * of legal text. Three of those documents are store-required.
 *
 * The parser below is deliberately small: it covers what `@tago/legal`'s
 * `toMarkdown()` emits plus the common inline marks, and it has no
 * dependencies, so a transitive package's module format can never again
 * decide whether the legal documents exist.
 *
 * Headings cascade `h1 → h2 → h3` with the same Heavitas/Montserrat
 * hierarchy as the rest of the app. Body text uses FONTS.body
 * (Montserrat-SemiBold) at the legal-document size (15/24).
 */

import React from 'react'
import { View, Text, Linking, Platform, type TextStyle, type ViewStyle } from 'react-native'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, SPACING, RADIUS } from '../../lib/theme/tokens'
import { COLORS, FONTS } from '../../lib/brand'

interface MarkdownViewProps {
  /** Raw markdown source. */
  source: string
  /** Optional max-width in pixels for desktop readability. */
  maxWidth?: number
}

// ─── Block model ────────────────────────────────────────────────────────────

type HeadingLevel = 1 | 2 | 3

type Block =
  | { kind: 'heading'; level: HeadingLevel; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'quote'; text: string }
  | { kind: 'rule' }

const HEADING_RE = /^(#{1,6})\s+(.*)$/
const RULE_RE = /^\s*([-*_])(?:\s*\1){2,}\s*$/
const BULLET_RE = /^\s*[-*+]\s+(.*)$/
const ORDERED_RE = /^\s*\d+[.)]\s+(.*)$/
const QUOTE_RE = /^\s*>\s?(.*)$/

/**
 * Markdown → blocks. Line-based, which is all the source needs: `toMarkdown()`
 * emits `## heading`, blank-line-separated paragraphs and `- ` bullets, and
 * keeps single newlines inside a paragraph as soft breaks.
 */
function parseBlocks(source: string): Block[] {
  const blocks: Block[] = []
  const lines = source.replace(/\r\n?/g, '\n').split('\n')

  let paragraph: string[] = []
  let quote: string[] = []
  let listOrdered = false
  let listItems: string[] = []

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', text: paragraph.join('\n') })
      paragraph = []
    }
  }
  const flushQuote = () => {
    if (quote.length > 0) {
      blocks.push({ kind: 'quote', text: quote.join('\n') })
      quote = []
    }
  }
  const flushList = () => {
    if (listItems.length > 0) {
      blocks.push({ kind: 'list', ordered: listOrdered, items: listItems })
      listItems = []
    }
  }
  const flushAll = () => {
    flushParagraph()
    flushQuote()
    flushList()
  }

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, '')

    if (line.trim().length === 0) {
      flushAll()
      continue
    }

    if (RULE_RE.test(line)) {
      flushAll()
      blocks.push({ kind: 'rule' })
      continue
    }

    const heading = HEADING_RE.exec(line)
    if (heading) {
      flushAll()
      const level = Math.min((heading[1] ?? '#').length, 3) as HeadingLevel
      blocks.push({ kind: 'heading', level, text: (heading[2] ?? '').trim() })
      continue
    }

    const quoted = QUOTE_RE.exec(line)
    if (quoted) {
      flushParagraph()
      flushList()
      quote.push(quoted[1] ?? '')
      continue
    }

    const bullet = BULLET_RE.exec(line)
    if (bullet) {
      flushParagraph()
      flushQuote()
      if (listOrdered) flushList()
      listOrdered = false
      listItems.push(bullet[1] ?? '')
      continue
    }

    const ordered = ORDERED_RE.exec(line)
    if (ordered) {
      flushParagraph()
      flushQuote()
      if (!listOrdered) flushList()
      listOrdered = true
      listItems.push(ordered[1] ?? '')
      continue
    }

    // Plain text. A line that continues a list item is folded into that item so
    // a wrapped bullet doesn't break out of the list.
    const openItemIndex = listItems.length - 1
    if (openItemIndex >= 0) {
      listItems[openItemIndex] = `${listItems[openItemIndex] ?? ''}\n${line.trim()}`
      continue
    }
    flushQuote()
    paragraph.push(line)
  }

  flushAll()
  return blocks
}

// ─── Inline model ───────────────────────────────────────────────────────────

type InlineToken =
  | { kind: 'text'; value: string }
  | { kind: 'strong'; value: string }
  | { kind: 'em'; value: string }
  | { kind: 'code'; value: string }
  | { kind: 'link'; value: string; href: string }

// Order matters: `**` has to be tried before `*`.
const INLINE_RE =
  /\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\n]+)\*|`([^`\n]+)`|\[([^\]\n]+)\]\(([^)\s]+)\)/

// The legal documents print their contact addresses as bare text
// ("privacy@tago.rent"). markdown-it linkified those for free; this keeps them
// tappable now that it is gone.
const AUTOLINK_RE = /(https?:\/\/[^\s<>()]+[^\s<>().,;:!?])|([\w.+-]+@[\w-]+\.[\w.-]*\w)/g

/** Push plain text, promoting bare URLs and email addresses to links. */
function pushText(tokens: InlineToken[], value: string): void {
  AUTOLINK_RE.lastIndex = 0
  let cursor = 0

  for (;;) {
    const match = AUTOLINK_RE.exec(value)
    if (!match) break
    if (match.index > cursor) {
      tokens.push({ kind: 'text', value: value.slice(cursor, match.index) })
    }
    const target = match[0]
    tokens.push({ kind: 'link', value: target, href: match[1] ? target : `mailto:${target}` })
    cursor = match.index + target.length
  }

  if (cursor < value.length) tokens.push({ kind: 'text', value: value.slice(cursor) })
}

function parseInline(input: string): InlineToken[] {
  const tokens: InlineToken[] = []
  let rest = input

  for (;;) {
    const match = INLINE_RE.exec(rest)
    if (!match) break

    const full = match[0]
    const strongA = match[1]
    const strongB = match[2]
    const em = match[3]
    const code = match[4]
    const linkText = match[5]
    const href = match[6]

    if (match.index > 0) {
      pushText(tokens, rest.slice(0, match.index))
    }

    if (strongA !== undefined || strongB !== undefined) {
      tokens.push({ kind: 'strong', value: strongA ?? strongB ?? '' })
    } else if (em !== undefined) {
      tokens.push({ kind: 'em', value: em })
    } else if (code !== undefined) {
      tokens.push({ kind: 'code', value: code })
    } else if (linkText !== undefined && href !== undefined) {
      tokens.push({ kind: 'link', value: linkText, href })
    }

    rest = rest.slice(match.index + full.length)
  }

  if (rest.length > 0) pushText(tokens, rest)
  return tokens
}

// ─── Rendering ──────────────────────────────────────────────────────────────

function InlineText({ content, style }: { content: string; style: TextStyle }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const tokens = parseInline(content)

  return (
    <Text style={style}>
      {tokens.map((token, index) => {
        const key = `${token.kind}-${index}`
        switch (token.kind) {
          case 'strong':
            return (
              <Text key={key} style={{ fontFamily: FONTS.bodyBold }}>
                {token.value}
              </Text>
            )
          case 'em':
            return (
              <Text key={key} style={{ fontStyle: 'italic' }}>
                {token.value}
              </Text>
            )
          case 'code':
            return (
              <Text
                key={key}
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  color: colors.textPrimary,
                  backgroundColor: colors.surfaceSubtle,
                }}
              >
                {token.value}
              </Text>
            )
          case 'link':
            return (
              <Linkable key={key} href={token.href}>
                {token.value}
              </Linkable>
            )
          default:
            return <Text key={key}>{token.value}</Text>
        }
      })}
    </Text>
  )
}

// Inline link. On web a plain <a> renders best (real hover, real middle-click);
// on native we open the URL through Linking.
function Linkable({ href, children }: { href: string; children: React.ReactNode }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: colors.textAccent, textDecoration: 'underline' }}
      >
        {children as React.ReactNode}
      </a>
    )
  }

  return (
    <Text
      accessibilityRole="link"
      onPress={() => {
        void Linking.openURL(href)
      }}
      style={{ color: colors.textAccent, textDecorationLine: 'underline' }}
    >
      {children}
    </Text>
  )
}

export function MarkdownView({ source, maxWidth }: MarkdownViewProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const bodyStyle: TextStyle = {
    fontFamily: FONTS.body,
    fontSize: 15,
    lineHeight: 24,
    color: colors.textPrimary,
  }

  const headingStyles: Record<HeadingLevel, TextStyle> = {
    1: {
      fontFamily: FONTS.bodyBold,
      fontSize: 28,
      lineHeight: 34,
      letterSpacing: -0.5,
      color: colors.textPrimary,
    },
    2: {
      fontFamily: FONTS.bodyBold,
      fontSize: 20,
      lineHeight: 28,
      letterSpacing: -0.3,
      color: colors.textPrimary,
    },
    3: {
      fontFamily: FONTS.bodyBold,
      fontSize: 17,
      lineHeight: 24,
      color: colors.textPrimary,
    },
  }

  const headingSpacing: Record<HeadingLevel, ViewStyle> = {
    1: { marginTop: SPACING.xl, marginBottom: SPACING.md },
    2: { marginTop: SPACING.xl, marginBottom: SPACING.sm },
    3: { marginTop: SPACING.lg, marginBottom: SPACING.xs },
  }

  const blocks = parseBlocks(source)

  return (
    <View style={maxWidth ? { maxWidth, alignSelf: 'center', width: '100%' } : undefined}>
      {blocks.map((block, index) => {
        const key = `${block.kind}-${index}`
        switch (block.kind) {
          case 'heading':
            return (
              <View key={key} style={headingSpacing[block.level]}>
                <InlineText content={block.text} style={headingStyles[block.level]} />
              </View>
            )

          case 'list':
            return (
              <View key={key} style={{ marginBottom: SPACING.md }}>
                {block.items.map((item, itemIndex) => (
                  <View
                    key={`${key}-${itemIndex}`}
                    style={{ flexDirection: 'row', marginBottom: SPACING.xs + 2 }}
                  >
                    <Text
                      style={{
                        marginRight: SPACING.sm,
                        color: colors.textSecondary,
                        fontFamily: FONTS.body,
                        fontSize: 15,
                        lineHeight: 24,
                      }}
                    >
                      {block.ordered ? `${itemIndex + 1}.` : '•'}
                    </Text>
                    <InlineText content={item} style={{ ...bodyStyle, flex: 1 }} />
                  </View>
                ))}
              </View>
            )

          case 'quote':
            return (
              <View
                key={key}
                style={{
                  backgroundColor: colors.surfaceSubtle,
                  borderLeftWidth: 3,
                  borderLeftColor: COLORS.purple.DEFAULT,
                  paddingHorizontal: SPACING.md,
                  paddingVertical: SPACING.sm,
                  marginBottom: SPACING.md,
                  borderRadius: RADIUS.sm,
                }}
              >
                <InlineText content={block.text} style={bodyStyle} />
              </View>
            )

          case 'rule':
            return (
              <View
                key={key}
                style={{
                  height: 1,
                  backgroundColor: colors.border,
                  marginVertical: SPACING.lg,
                }}
              />
            )

          default:
            return (
              <InlineText
                key={key}
                content={block.text}
                style={{ ...bodyStyle, marginBottom: SPACING.md }}
              />
            )
        }
      })}
    </View>
  )
}
